import type { ConnectionStatus, EventFrame, ResponseFrame, ConnectParams } from '../types/gateway';

function getGatewayUrl(): string {
  const envUrl = import.meta.env.VITE_GATEWAY_URL;
  if (envUrl) return envUrl;
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/ws`;
}

const GATEWAY_URL = getGatewayUrl();
const GATEWAY_TOKEN = import.meta.env.VITE_GATEWAY_TOKEN || '';

let idCounter = 0;
function nextId(): string {
  return `h${Date.now().toString(36)}-${(++idCounter).toString(36)}`;
}

export type MessageHandler = (data: unknown) => void;
export type EventHandler = (event: EventFrame) => void;
export type StatusHandler = (status: ConnectionStatus) => void;

interface PendingRequest {
  resolve: (result: unknown) => void;
  reject: (error: Error) => void;
}

export class GatewayConnection {
  private ws: WebSocket | null = null;
  private eventHandlers: Set<EventHandler> = new Set();
  private messageHandlers: Set<MessageHandler> = new Set();
  private statusHandlers: Set<StatusHandler> = new Set();
  private pending: Map<string, PendingRequest> = new Map();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 15;
  connectNonce: string | null = null;
  private connectSent = false;
  private _status: ConnectionStatus = 'disconnected';
  private _connected = false;

  get status(): ConnectionStatus {
    return this._status;
  }

  get connected(): boolean {
    return this._connected;
  }

  connect(): void {
    this.reconnectAttempts = 0;
    this.doConnect();
  }

  private doConnect(): void {
    if (this.ws?.readyState === WebSocket.OPEN || this.ws?.readyState === WebSocket.CONNECTING) return;

    this._connected = false;
    this.connectNonce = null;
    this.connectSent = false;
    this.setStatus('connecting');

    try {
      this.ws = new WebSocket(GATEWAY_URL);

      this.ws.onopen = () => {
        this.reconnectAttempts = 0;
        // Do NOT send anything yet — wait for connect.challenge from server
      };

      this.ws.onmessage = (event) => {
        this.handleMessage(event.data);
      };

      this.ws.onclose = (event) => {
        this._connected = false;
        this.flushPending(new Error(`WebSocket closed: ${event.code} ${event.reason}`));
        this.setStatus('disconnected');
        this.scheduleReconnect();
      };

      this.ws.onerror = () => {
        // onerror is always followed by onclose
      };
    } catch {
      this.setStatus('error');
      this.scheduleReconnect();
    }
  }

  private handleMessage(raw: string): void {
    let frame: Record<string, unknown>;
    try {
      frame = JSON.parse(raw);
    } catch {
      console.error('[Gateway] Failed to parse:', raw);
      return;
    }

    // Debug logging (uncomment if needed):
    // console.log('[Gateway] RECV:', frame.type, (frame as Record<string, unknown>).event || (frame as Record<string, unknown>).id || '');

    // ── Event frames ──────────────────────────────────────────────────────
    if (frame.type === 'event') {
      const evt = frame as unknown as EventFrame;

      // Handle connect challenge
      if (evt.event === 'connect.challenge') {
        const payload = evt.payload as { nonce?: string } | undefined;
        const nonce = payload?.nonce;
        if (typeof nonce === 'string') {
          this.connectNonce = nonce;
          this.sendConnect();
        }
        return;
      }

      // Forward all other events to handlers
      this.eventHandlers.forEach((h) => h(evt));
      this.messageHandlers.forEach((h) => h(evt));
      return;
    }

    // ── Response frames ───────────────────────────────────────────────────
    if (frame.type === 'res') {
      const res = frame as unknown as ResponseFrame;
      const id = res.id;
      const pending = this.pending.get(id);
      if (pending) {
        this.pending.delete(id);
        if (res.error) {
          const err = new Error(res.error.message);
          (err as Error & { code?: string }).code = res.error.code;
          pending.reject(err);
        } else {
          // OpenClaw uses .payload (not .result) for response data
          pending.resolve(res.payload ?? res.result);
        }
      }
      return;
    }

    // Forward anything else to legacy message handlers
    this.messageHandlers.forEach((h) => h(frame));
  }

  private async sendConnect(): Promise<void> {
    if (this.connectSent) return;
    this.connectSent = true;

    const params: ConnectParams = {
      // Gateway v2026.5.18+ requires protocol 4. Accept either so we still
      // work if anyone downgrades the gateway back to a v3-only build.
      minProtocol: 3,
      maxProtocol: 4,
      client: {
        // CRITICAL: must be 'openclaw-control-ui' (or 'openclaw-tui') for the
        // gateway's isControlUi check to grant requested scopes. Any other
        // value (e.g. our old 'webchat-ui') causes the gateway to silently
        // clear scopes after connect, breaking every chat.send / sessions.list
        // with "missing scope: operator.read". Found by reading
        // /usr/lib/node_modules/openclaw/dist/message-channel-*.js:34
        // (isOperatorUiClient) on the VPS. Mode 'webchat' is still correct.
        id: 'openclaw-control-ui',
        version: '1.0.0',
        platform: navigator.platform || 'web',
        mode: 'webchat',
      },
      role: 'operator',
      // Gateway v2026.5.18 (protocol v4) requires read/write scopes to be
      // requested explicitly — in v3 they were implicit. Without these, every
      // chat.send / chat.history call comes back with INVALID_REQUEST and the
      // UI shows no response.
      scopes: ['operator.read', 'operator.write', 'operator.admin', 'operator.approvals', 'operator.pairing'],
      auth: GATEWAY_TOKEN ? { token: GATEWAY_TOKEN } : undefined,
      caps: [],
      userAgent: navigator.userAgent,
      locale: navigator.language,
    };

    try {
      await this.request('connect', params as unknown as Record<string, unknown>);
      this._connected = true;
      this.setStatus('connected');
    } catch (err) {
      console.error('[Gateway] connect failed:', err);
      this._connected = false;
      this.ws?.close(4000, 'connect failed');
    }
  }

  /** Send a request and await the response */
  request(method: string, params: Record<string, unknown>): Promise<unknown> {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      // console.error('[Gateway] request() rejected: ws not open, readyState=', this.ws?.readyState);
      return Promise.reject(new Error('gateway not connected'));
    }

    const id = nextId();
    const frame = { type: 'req', id, method, params };
    // console.log('[Gateway] SEND:', method, id);

    return new Promise((resolve, reject) => {
      this.pending.set(id, { resolve, reject });
      this.ws!.send(JSON.stringify(frame));
    });
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) return;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);

    const delay = Math.min(800 * Math.pow(1.7, this.reconnectAttempts), 15000);
    this.reconnectAttempts++;

    this.reconnectTimer = setTimeout(() => {
      this.doConnect();
    }, delay);
  }

  private flushPending(error: Error): void {
    for (const [, p] of this.pending) {
      p.reject(error);
    }
    this.pending.clear();
  }

  /** Send raw data (low-level) */
  send(data: unknown): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    }
  }

  onMessage(handler: MessageHandler): () => void {
    this.messageHandlers.add(handler);
    return () => this.messageHandlers.delete(handler);
  }

  onEvent(handler: EventHandler): () => void {
    this.eventHandlers.add(handler);
    return () => this.eventHandlers.delete(handler);
  }

  onStatusChange(handler: StatusHandler): () => void {
    this.statusHandlers.add(handler);
    return () => this.statusHandlers.delete(handler);
  }

  private setStatus(status: ConnectionStatus): void {
    this._status = status;
    this.statusHandlers.forEach((handler) => handler(status));
  }

  disconnect(): void {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this._connected = false;
    this.flushPending(new Error('disconnected'));
    this.ws?.close();
    this.ws = null;
    this.setStatus('disconnected');
  }
}

// Singleton instance
export const gateway = new GatewayConnection();
